import { com } from '@eidu/web'
import { anchorTypeIdOf } from '../../components/permissions/utils'
import actionLabel from './actionLabel'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntitySelector = com.eidu.entity.selector.EntitySelector
import GlobalAction = com.eidu.entity.permission.GlobalAction
import EntityAction = com.eidu.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.entity.permission.EntitySelectorPermission
import GlobalPermission = com.eidu.entity.permission.GlobalPermission
import Action = com.eidu.entity.permission.Action
import Permission = com.eidu.entity.permission.Permission

export type ActionsBreakdown = {
  global: string[]
  perType: { typeName: string; labels: string[] }[]
}

const GLOBAL_ACTION_ORDER: Action[] = [...GlobalAction.values(), EntityAction.ShareSchoolLogin]

const ENTITY_ACTION_ORDER: EntityAction[] = [
  EntityAction.CreateEntity,
  EntityAction.ReadEntity,
  EntityAction.UpdateEntity,
  EntityAction.DeleteEntity,
  EntityAction.PublishAsset,
  EntityAction.SubmitForm,
  EntityAction.ViewFormSubmissions,
]

// EntityActions rendered in the global breakdown rather than per type. Only ShareSchoolLogin remains;
// SubmitForm / ViewFormSubmissions are now scoped per-form entity actions and appear under their type.
const GLOBAL_ENTITY_ACTIONS: Set<EntityAction> = new Set([EntityAction.ShareSchoolLogin])

const ALL_TYPES_KEY = '__all__'
const ALL_TYPES_LABEL = 'All'

const computeActionsBreakdown = (
  permissions: ReadonlyArray<Permission>,
  entityTypes: ReadonlyMap<EntityTypeId, EntityType | null>
): ActionsBreakdown => {
  const globalSet = new Set<Action>()
  const perTypeActions = new Map<string, { typeName: string; actions: Set<EntityAction> }>()
  const types = Array.from(entityTypes.values()).filter((t): t is EntityType => t !== null)

  for (const permission of permissions) {
    if (permission instanceof GlobalPermission) {
      globalSet.add(permission.action)
      continue
    }
    if (permission instanceof EntitySelectorPermission && EntitySelector.All.equals(permission.targets)) {
      if (GLOBAL_ENTITY_ACTIONS.has(permission.action)) {
        globalSet.add(permission.action)
      } else {
        const entry = perTypeActions.get(ALL_TYPES_KEY) ?? {
          typeName: ALL_TYPES_LABEL,
          actions: new Set<EntityAction>(),
        }
        entry.actions.add(permission.action as EntityAction)
        perTypeActions.set(ALL_TYPES_KEY, entry)
      }
      continue
    }
    const anchor = anchorTypeIdOf(permission, types)
    if (!anchor) continue
    const idStr = anchor.toString()
    const entry = perTypeActions.get(idStr) ?? {
      typeName: String(entityTypes.get(anchor)?.name ?? `Unknown (${idStr})`),
      actions: new Set<EntityAction>(),
    }
    entry.actions.add(permission.action as EntityAction)
    perTypeActions.set(idStr, entry)
  }

  const global = GLOBAL_ACTION_ORDER.filter((a) => globalSet.has(a)).map(actionLabel)
  const perType = Array.from(perTypeActions.values())
    .map(({ typeName, actions }) => ({
      typeName,
      labels: ENTITY_ACTION_ORDER.filter((a) => actions.has(a)).map(actionLabel),
    }))
    .sort((a, b) => {
      if (a.typeName === ALL_TYPES_LABEL) return -1
      if (b.typeName === ALL_TYPES_LABEL) return 1
      return a.typeName.localeCompare(b.typeName)
    })

  return { global, perType }
}

export default computeActionsBreakdown
